import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '../ui/Button';
import { useSimulationStore } from '../../store/useSimulationStore';
import {
  Compass,
  ArrowRight,
  ArrowLeft,
  ArrowDown,
  X,
  Sparkles,
  Check,
  CheckCircle2,
  MousePointerClick,
  Eye,
} from 'lucide-react';

export interface OnboardingTourProps {
  isOpen: boolean;
  onClose: () => void;
  startDirectly?: boolean;
  isNodeEditorOpen?: boolean;
  onOpenNodeEditor?: (nodeId: string) => void;
  onCloseNodeEditor?: () => void;
  onNavigateTab?: (tab: 'map' | 'metrics' | 'check' | 'settings') => void;
}

export type TourStepPhase = 'action' | 'observation';

export interface TourStepDefinition {
  id: string;
  actionSelectors: string[];
  observationSelectors?: string[];
  actionTitle: string;
  actionDescription: string;
  actionHint?: string;
  observationTitle: string;
  observationDescription: string;
  observationHint?: string;
  preferredPlacement?: 'top' | 'bottom' | 'left' | 'right';
  observationPreferredPlacement?: 'top' | 'bottom' | 'left' | 'right';
  interactive?: boolean; // If false, purely observation step (e.g. step 6)
  actionButtonText?: string;
  observationButtonText?: string;
}

export const TOUR_STEPS: TourStepDefinition[] = [
  // Step 1: Mode Selector -> Switch to Sandbox
  {
    id: 'mode-selector',
    actionSelectors: ['[data-tour="mode-sandbox-btn"]', '[data-tour="mode-selector"]'],
    observationSelectors: ['[data-tour="mode-sandbox-btn"]', '[data-tour="mode-selector"]'],
    actionTitle: 'Крок 1: Режими роботи — Конструктор структури',
    actionDescription:
      'Натисніть на «Конструктор структури», щоб перейти в режим вільного проектування команди.',
    actionHint: 'Клікніть на підсвічену кнопку «Конструктор структури» вгорі',
    observationTitle: 'Чудово! Режим Конструктора активовано',
    observationDescription:
      'Ви перейшли в Конструктор структури. Зверніть увагу: тепер ви можете вільно додавати та редагувати партнерів, змінювати кар’єрні рівні (Р1–Р8) та моделювати власні гілки без часових обмежень.',
    observationHint:
      'Ознайомтеся з інтерфейсом та натисніть «Далі ➔», щоб перейти до роботи з консультантами',
    preferredPlacement: 'bottom',
    observationPreferredPlacement: 'bottom',
    interactive: true,
    actionButtonText: 'Перейти в Конструктор ➔',
    observationButtonText: 'Далі ➔',
  },
  // Step 2: Structure Canvas & Consultant Node -> Open Node Editor
  {
    id: 'consultant-node',
    actionSelectors: ['[data-tour="consultant-node"]', '[data-tour="structure-canvas"]'],
    observationSelectors: [
      '[data-tour="node-kpi-metrics"]',
      '[data-tour="node-editor-drawer"]',
      '[data-tour="structure-canvas"]',
    ],
    actionTitle: 'Крок 2: Інтерактивна мапа та консультанти',
    actionDescription:
      'Клікніть на будь-якого консультанта на мапі, щоб відкрити панель детального редагування його показників.',
    actionHint: 'Натисніть на підсвічене коло консультанта на схемі',
    observationTitle: 'Панель показників та 6 KPI консультанта',
    observationDescription:
      'Перед вами відкрилася картка консультанта з 6 ключовими показниками (Особисті ОЕ, Структурні ОЕ, Рівень, Чек тощо). У цьому режимі ви можете коригувати обсяги зустрічей, конверсію та рівні.',
    observationHint:
      'Коли будете готові, натисніть «Далі ➔», щоб повернутися до динамічної симуляції кар’єри',
    preferredPlacement: 'bottom',
    observationPreferredPlacement: 'left',
    interactive: true,
    actionButtonText: 'Відкрити картку консультанта ➔',
    observationButtonText: 'Далі ➔',
  },
  // Step 3: Switch back to Pathway Simulation
  {
    id: 'switch-to-pathway',
    actionSelectors: ['[data-tour="mode-pathway-btn"]', '[data-tour="mode-selector"]'],
    observationSelectors: ['[data-tour="mode-pathway-btn"]', '[data-tour="mode-selector"]'],
    actionTitle: 'Крок 3: Повернення до «Шляху новачка»',
    actionDescription:
      'Тепер натисніть на «Шлях новачка (1–60 міс)», щоб увімкнути математичну динамічну симуляцію кар’єрного зростання.',
    actionHint: 'Натисніть на кнопку «Шлях новачка» в панелі режимів',
    observationTitle: 'Динамічна помісячна симуляція',
    observationDescription:
      'Симулятор автоматично змоделював розвиток структури від 1-го до 60-го місяця за маркетинг-планом Starlife: підключення нових консультантів, зростання команди та накопичення кар’єрних одиниць.',
    observationHint: 'Натисніть «Далі ➔», щоб навчитися керувати часовою шкалою симуляції',
    preferredPlacement: 'bottom',
    observationPreferredPlacement: 'bottom',
    interactive: true,
    actionButtonText: 'Увімкнути Шлях новачка ➔',
    observationButtonText: 'Далі ➔',
  },
  // Step 4: Timeline / Simulation Controls (MonthSlider)
  {
    id: 'timeline-controls',
    actionSelectors: [
      '[data-tour="milestone-12m"]',
      '[data-tour="timeline-play-btn"]',
      '[data-tour="timeline-controls"]',
    ],
    observationSelectors: ['[data-tour="timeline-controls"]'],
    actionTitle: 'Крок 4: Часова шкала та динаміка команди',
    actionDescription:
      'Клікніть на позначку «12м» (або кнопку Play ▶), щоб миттєво переміститися на 12-й місяць розвитку структури.',
    actionHint: 'Натисніть на кнопку «12м» на панелі шкали місяців',
    observationTitle: 'Рік у бізнесі: Рівень та Чек',
    observationDescription:
      'Подивіться, як змінилася команда за 12 місяців: з’явилися нові прямі лінії, зріс кар’єрний рівень лідера та суттєво збільшився щомісячний дохід у гривнях!',
    observationHint: 'Натисніть «Далі ➔», щоб ознайомитися з нарахуванням винагороди',
    preferredPlacement: 'right',
    observationPreferredPlacement: 'right',
    interactive: true,
    actionButtonText: 'Перейти на 12-й місяць ➔',
    observationButtonText: 'Далі ➔',
  },
  // Step 5: Commission Rate Toggle (CheckBreakdownTable)
  {
    id: 'commission-toggle',
    actionSelectors: [
      '[data-tour="commission-80-btn"]',
      '[data-tour="commission-100-btn"]',
      '[data-tour="commission-toggle"]',
    ],
    observationSelectors: ['[data-tour="commission-toggle"]'],
    actionTitle: 'Крок 5: Коефіцієнт комісії (100% / 80%)',
    actionDescription:
      'Перемкніть коефіцієнт на «80% (1-й рік)» (або «100%»), щоб побачити вплив резервування виплат першого року.',
    actionHint: 'Натисніть на перемикач «80%» або «100%» у панелі чеку',
    observationTitle: 'Прозорий розрахунок виплат',
    observationDescription:
      'У Starlife 80% комісії виплачується у перший рік, а 20% резервується до підтвердження договорів. Директорські та співвласницькі бонуси завжди нараховуються на 100% без утримань!',
    observationHint: 'Натисніть «Далі ➔», щоб перейти до збереження та експорту звітів',
    preferredPlacement: 'left',
    observationPreferredPlacement: 'left',
    interactive: true,
    actionButtonText: 'Перемкнути коефіцієнт ➔',
    observationButtonText: 'Далі ➔',
  },
  // Step 6: Cloud Library & PDF Document
  {
    id: 'document-export',
    actionSelectors: ['[data-tour="document-export"]'],
    observationSelectors: ['[data-tour="document-export"]'],
    actionTitle: 'Крок 6: Хмарне збереження та PDF-звіти',
    actionDescription:
      'Усі ваші розрахунки можна в один клік зберегти у хмарну бібліотеку (Cloudflare KV) або експортувати у горизонтальний PDF-документ.',
    observationTitle: 'Крок 6: Хмарне збереження та PDF-звіти',
    observationDescription:
      'Усі ваші розрахунки можна в один клік зберегти у хмарну бібліотеку (Cloudflare KV) або експортувати у презентабельний горизонтальний багатосторінковий PDF-документ з візуальною мапою команди, кваліфікаційними цілями та детальним розрахунковим чеком.',
    observationHint:
      'Ви готові до повноцінної роботи з симулятором! Бажаємо високих чеків та стрімкого кар’єрного зростання!',
    preferredPlacement: 'bottom',
    observationPreferredPlacement: 'bottom',
    interactive: false,
    observationButtonText: 'Завершити навчання',
  },
];

export const ONBOARDING_STORAGE_KEY = 'starlife_onboarding_completed_v2';

/**
 * Utility to find the first visible matching element in DOM across desktop and tablet
 */
export function findVisibleElement(selectors: string[]): HTMLElement | null {
  if (typeof document === 'undefined') return null;

  for (const selector of selectors) {
    try {
      const elements = Array.from(document.querySelectorAll(selector)) as HTMLElement[];
      for (const el of elements) {
        const rect = el.getBoundingClientRect();
        if (
          rect.width > 0 &&
          rect.height > 0 &&
          Number.isFinite(rect.left) &&
          Number.isFinite(rect.top)
        ) {
          return el;
        }
      }
    } catch {
      // Continue searching
    }
  }
  return null;
}

export const OnboardingTour: React.FC<OnboardingTourProps> = ({
  isOpen,
  onClose,
  startDirectly = false,
  isNodeEditorOpen = false,
  onOpenNodeEditor,
  onCloseNodeEditor,
  onNavigateTab,
}) => {
  const [phase, setPhase] = useState<'prompt' | 'tour'>(startDirectly ? 'tour' : 'prompt');
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [stepPhase, setStepPhase] = useState<TourStepPhase>('action');
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [measuredTooltipH, setMeasuredTooltipH] = useState(260);
  const tooltipRef = useRef<HTMLDivElement>(null);

  // Guarantee that when startDirectly is true, the tour is always the active phase immediately
  const effectivePhase = startDirectly ? 'tour' : phase;

  // Stabilize callbacks with refs to avoid unwanted effect loops
  const onOpenNodeEditorRef = useRef(onOpenNodeEditor);
  onOpenNodeEditorRef.current = onOpenNodeEditor;
  const onCloseNodeEditorRef = useRef(onCloseNodeEditor);
  onCloseNodeEditorRef.current = onCloseNodeEditor;
  const onNavigateTabRef = useRef(onNavigateTab);
  onNavigateTabRef.current = onNavigateTab;

  // Store bindings
  const mode = useSimulationStore((state) => state.mode);
  const setMode = useSimulationStore((state) => state.setMode);
  const selectedMonth = useSimulationStore((state) => state.selectedMonth);
  const setSelectedMonth = useSimulationStore((state) => state.setSelectedMonth);
  const commissionRateCoefficient = useSimulationStore((state) => state.commissionRateCoefficient);
  const setCommissionRateCoefficient = useSimulationStore(
    (state) => state.setCommissionRateCoefficient
  );
  const getCurrentRootNode = useSimulationStore((state) => state.getCurrentRootNode);

  // Timer tracking to prevent leftover background retries from racing across step transitions
  const pendingTimeoutsRef = useRef<number[]>([]);
  const clearPendingTimeouts = useCallback(() => {
    pendingTimeoutsRef.current.forEach((id) => clearTimeout(id));
    pendingTimeoutsRef.current = [];
  }, []);

  useEffect(() => {
    return () => {
      clearPendingTimeouts();
    };
  }, [clearPendingTimeouts]);

  const resetSimulatorToTourBaseline = useCallback(() => {
    setMode('pathway');
    setSelectedMonth(1);
    setCommissionRateCoefficient(1.0);
    onCloseNodeEditorRef.current?.();
  }, [setMode, setSelectedMonth, setCommissionRateCoefficient]);

  // Reset or initialize on open
  useEffect(() => {
    if (isOpen) {
      if (!startDirectly) {
        setPhase('prompt');
      } else {
        setPhase('tour');
      }
      setCurrentStepIndex(0);
      setStepPhase('action');
      if (startDirectly) {
        resetSimulatorToTourBaseline();
      }
    }
  }, [isOpen, startDirectly, resetSimulatorToTourBaseline]);

  // Target rectangle calculation with active selectors (stable reference & deep equality check)
  const updateTargetRect = useCallback(() => {
    if (effectivePhase !== 'tour' || !isOpen || typeof document === 'undefined') {
      setTargetRect((prev) => (prev === null ? prev : null));
      return;
    }

    const step = TOUR_STEPS[currentStepIndex];
    if (!step) return;

    const selectors =
      stepPhase === 'observation' && step.observationSelectors
        ? step.observationSelectors
        : step.actionSelectors;

    const el = findVisibleElement(selectors);
    if (el) {
      const rect = el.getBoundingClientRect();
      if (
        rect.width > 0 &&
        rect.height > 0 &&
        Number.isFinite(rect.left) &&
        Number.isFinite(rect.top)
      ) {
        setTargetRect((prev) => {
          if (
            prev &&
            Math.abs(prev.left - rect.left) < 1 &&
            Math.abs(prev.top - rect.top) < 1 &&
            Math.abs(prev.width - rect.width) < 1 &&
            Math.abs(prev.height - rect.height) < 1
          ) {
            return prev;
          }
          return rect;
        });
        return;
      }
    }
    setTargetRect((prev) => (prev === null ? prev : null));
  }, [effectivePhase, isOpen, currentStepIndex, stepPhase]);

  // Ensure DOM container panels or tabs are mounted/active before querying
  const prepareTourStep = useCallback(
    (index: number, targetPhase: TourStepPhase) => {
      if (typeof document === 'undefined') return;
      const step = TOUR_STEPS[index];
      if (!step) return;

      clearPendingTimeouts();

      try {
        const storeState = useSimulationStore.getState();
        if (step.id === 'mode-selector') {
          if (targetPhase === 'action' && storeState.mode !== 'pathway') {
            storeState.setMode('pathway');
          }
          if (targetPhase === 'observation' && storeState.mode !== 'sandbox') {
            storeState.setMode('sandbox');
          }
          onCloseNodeEditorRef.current?.();
        } else if (step.id === 'consultant-node') {
          if (storeState.mode !== 'sandbox') storeState.setMode('sandbox');
          if (targetPhase === 'action' && isNodeEditorOpen) {
            onCloseNodeEditorRef.current?.();
          }
          if (targetPhase === 'observation' && !isNodeEditorOpen) {
            const root = storeState.getCurrentRootNode();
            onOpenNodeEditorRef.current?.(root.id);
          }
          // Ensure canvas is visible on mobile
          onNavigateTabRef.current?.('map');
          const mapTab = Array.from(document.querySelectorAll('button')).find(
            (b) => b.textContent?.trim() === 'Мапа'
          );
          if (mapTab) mapTab.click();
        } else if (step.id === 'switch-to-pathway') {
          // If drawer is open, close it to uncover the header mode selector
          onCloseNodeEditorRef.current?.();
          if (targetPhase === 'action' && storeState.mode !== 'sandbox') {
            storeState.setMode('sandbox');
          }
          if (targetPhase === 'observation' && storeState.mode !== 'pathway') {
            storeState.setMode('pathway');
          }
        } else if (step.id === 'timeline-controls') {
          if (storeState.mode !== 'pathway') storeState.setMode('pathway');
          if (targetPhase === 'action' && storeState.selectedMonth === 12) {
            storeState.setSelectedMonth(1);
          }
          if (targetPhase === 'observation' && storeState.selectedMonth !== 12) {
            storeState.setSelectedMonth(12);
          }
          // Ensure mobile settings tab or left sidebar is open
          onNavigateTabRef.current?.('settings');
          const tabs = Array.from(document.querySelectorAll('button'));
          const settingsTab = tabs.find((b) => b.textContent?.trim() === 'Параметри');
          if (settingsTab) settingsTab.click();

          const expandLeft = document.querySelector(
            'button[title*="параметрів"]'
          ) as HTMLButtonElement | null;
          if (expandLeft) expandLeft.click();
        } else if (step.id === 'commission-toggle') {
          if (targetPhase === 'action' && storeState.commissionRateCoefficient !== 1.0) {
            storeState.setCommissionRateCoefficient(1.0);
          }
          if (targetPhase === 'observation' && storeState.commissionRateCoefficient !== 0.8) {
            storeState.setCommissionRateCoefficient(0.8);
          }
          // Ensure Check tab is active
          onNavigateTabRef.current?.('check');
          const tabs = Array.from(document.querySelectorAll('button'));
          const checkTab = tabs.find((b) => b.textContent?.includes('Деталізація чеку'));
          if (checkTab) checkTab.click();

          const tabletCheck = tabs.find((b) => b.textContent?.trim() === 'Чек');
          if (tabletCheck) tabletCheck.click();

          const expandRight = document.querySelector(
            'button[title*="аналітику"]'
          ) as HTMLButtonElement | null;
          if (expandRight) expandRight.click();
        }
      } catch {
        // Ignore DOM query errors in tests or edge cases
      }

      // Allow DOM transitions to settle, scroll target into view, then update bounding rect with retries
      [30, 100, 250, 450].forEach((delay) => {
        const id = window.setTimeout(() => {
          if (typeof document !== 'undefined') {
            const selectors =
              targetPhase === 'observation' && step.observationSelectors
                ? step.observationSelectors
                : step.actionSelectors;
            const el = findVisibleElement(selectors);
            if (el && delay === 30) {
              try {
                el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
              } catch {
                // Ignore
              }
            }
          }
          updateTargetRect();
        }, delay);
        pendingTimeoutsRef.current.push(id);
      });
    },
    [updateTargetRect, isNodeEditorOpen, clearPendingTimeouts]
  );

  // Sync step preparation
  useEffect(() => {
    if (effectivePhase === 'tour' && isOpen) {
      prepareTourStep(currentStepIndex, stepPhase);
    }
  }, [effectivePhase, isOpen, currentStepIndex, stepPhase, prepareTourStep]);

  // Window resize & scroll listener for continuous target tracking
  useEffect(() => {
    updateTargetRect();
    const handleResize = () => updateTargetRect();
    const handleScroll = () => updateTargetRect();
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [updateTargetRect]);

  // Auto-detect user's real actions in the UI and automatically switch to observation phase
  useEffect(() => {
    if (effectivePhase !== 'tour' || stepPhase !== 'action') return;

    if (currentStepIndex === 0 && mode === 'sandbox') {
      // User switched to sandbox
      setStepPhase('observation');
    } else if (currentStepIndex === 1 && isNodeEditorOpen) {
      // User opened node editor
      setStepPhase('observation');
    } else if (currentStepIndex === 2 && mode === 'pathway') {
      // User switched to pathway
      setStepPhase('observation');
    } else if (currentStepIndex === 3 && selectedMonth === 12) {
      // User jumped to month 12
      setStepPhase('observation');
    } else if (currentStepIndex === 4 && commissionRateCoefficient === 0.8) {
      // User switched commission to 80%
      setStepPhase('observation');
    }
  }, [
    effectivePhase,
    stepPhase,
    currentStepIndex,
    mode,
    isNodeEditorOpen,
    selectedMonth,
    commissionRateCoefficient,
  ]);

  // Tour lifecycle triggers
  const handleStartTour = () => {
    resetSimulatorToTourBaseline();
    setPhase('tour');
    setCurrentStepIndex(0);
    setStepPhase('action');
    prepareTourStep(0, 'action');
  };

  const handleComplete = () => {
    try {
      localStorage.setItem(ONBOARDING_STORAGE_KEY, 'true');
    } catch {
      // Ignore
    }
    onCloseNodeEditorRef.current?.();
    onClose();
  };

  const handleSkip = () => {
    handleComplete();
  };

  // Perform the interactive action and reveal observation without skipping
  const handleExecuteAction = () => {
    try {
      if (currentStepIndex === 0) {
        setMode('sandbox');
        const btn = findVisibleElement(['[data-tour="mode-sandbox-btn"]']);
        if (btn) btn.click();
      } else if (currentStepIndex === 1) {
        const targetNode = findVisibleElement(['[data-tour="consultant-node"]']);
        const nodeId = targetNode?.getAttribute('data-node-id') || getCurrentRootNode().id;
        if (onOpenNodeEditorRef.current) {
          onOpenNodeEditorRef.current(nodeId);
        } else if (targetNode) {
          targetNode.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
        }
      } else if (currentStepIndex === 2) {
        onCloseNodeEditorRef.current?.();
        setMode('pathway');
        const btn = findVisibleElement(['[data-tour="mode-pathway-btn"]']);
        if (btn) btn.click();
      } else if (currentStepIndex === 3) {
        setSelectedMonth(12);
        const btn = findVisibleElement(['[data-tour="milestone-12m"]']);
        if (btn) btn.click();
      } else if (currentStepIndex === 4) {
        setCommissionRateCoefficient(0.8);
        const btn = findVisibleElement(['[data-tour="commission-80-btn"]']);
        if (btn) btn.click();
      }
    } catch {
      // Safe fallback
    }

    setStepPhase('observation');
  };

  // Step advancement logic: action -> observation -> next step
  const handleNext = () => {
    const step = TOUR_STEPS[currentStepIndex];
    if (!step) return;

    if (step.interactive && stepPhase === 'action') {
      // Execute the real action first and show observation; never jump past observation!
      handleExecuteAction();
      return;
    }

    if (currentStepIndex < TOUR_STEPS.length - 1) {
      const nextIdx = currentStepIndex + 1;
      // If advancing from step 1 (consultant inspection) to step 2, close drawer so mode selector is unobstructed
      if (currentStepIndex === 1) {
        onCloseNodeEditorRef.current?.();
      }
      setCurrentStepIndex(nextIdx);
      setStepPhase('action');
      prepareTourStep(nextIdx, 'action');
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (stepPhase === 'observation' && TOUR_STEPS[currentStepIndex]?.interactive) {
      // Step back to action phase of the current step
      // Reset this step's state so action can be repeated cleanly
      if (currentStepIndex === 0) {
        setMode('pathway');
      } else if (currentStepIndex === 1) {
        onCloseNodeEditorRef.current?.();
      } else if (currentStepIndex === 2) {
        setMode('sandbox');
      } else if (currentStepIndex === 3) {
        setSelectedMonth(1);
      } else if (currentStepIndex === 4) {
        setCommissionRateCoefficient(1.0);
      }
      setStepPhase('action');
      prepareTourStep(currentStepIndex, 'action');
    } else if (currentStepIndex > 0) {
      const prevIdx = currentStepIndex - 1;
      setCurrentStepIndex(prevIdx);
      setStepPhase('observation');
      // Set previous step's observation state
      if (prevIdx === 0) {
        setMode('sandbox');
      } else if (prevIdx === 1) {
        setMode('sandbox');
        const root = getCurrentRootNode();
        onOpenNodeEditorRef.current?.(root.id);
      } else if (prevIdx === 2) {
        setMode('pathway');
        onCloseNodeEditorRef.current?.();
      } else if (prevIdx === 3) {
        setMode('pathway');
        setSelectedMonth(12);
      } else if (prevIdx === 4) {
        setCommissionRateCoefficient(0.8);
      }
      prepareTourStep(prevIdx, 'observation');
    }
  };

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleSkip();
      } else if (e.key === 'ArrowRight' && effectivePhase === 'tour') {
        handleNext();
      } else if (e.key === 'ArrowLeft' && effectivePhase === 'tour') {
        handlePrev();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, effectivePhase, currentStepIndex, stepPhase]);

  // Track dynamic tooltip container height for accurate boundary clamping and placement
  useEffect(() => {
    if (tooltipRef.current) {
      const h = tooltipRef.current.offsetHeight;
      if (h > 0 && Math.abs(h - measuredTooltipH) > 4) {
        setMeasuredTooltipH(h);
      }
    }
  }, [currentStepIndex, stepPhase, effectivePhase, measuredTooltipH]);

  if (!isOpen) return null;

  // Initial Welcome Prompt Modal
  if (effectivePhase === 'prompt') {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75 backdrop-blur-md animate-fadeIn">
        <div className="w-full max-w-md rounded-2xl bg-gradient-to-b from-slate-900 via-[#0D1527] to-slate-900 border border-cyan-500/30 p-6 shadow-[0_0_50px_rgba(6,182,212,0.25)] space-y-5">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-tr from-cyan-500/20 to-emerald-500/20 border border-cyan-400/40 flex items-center justify-center text-cyan-400 shadow-[0_0_15px_rgba(6,182,212,0.3)] flex-shrink-0">
              <Compass className="w-6 h-6 animate-pulse" />
            </div>
            <div>
              <h3 className="text-base sm:text-lg font-bold text-white flex items-center gap-2">
                <span>Ласкаво просимо!</span>
                <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-500/20 text-accent-neon font-mono font-medium">
                  Starlife Pro
                </span>
              </h3>
              <p className="text-xs text-text-secondary">
                Симулятор кар'єри та маркетингової структури
              </p>
            </div>
          </div>

          <div className="space-y-2 text-xs text-slate-300 leading-relaxed bg-slate-950/60 p-4 rounded-xl border border-white/5">
            <p className="font-medium text-white">
              Бажаєте пройти коротке інтерактивне навчання по ключових функціях симулятора?
            </p>
            <p className="text-text-muted text-[11px]">
              Ми покажемо, як перемикати режими симуляції, гнучко налаштовувати лідера та команду,
              аналізувати чек та експортувати готові PDF-звіти.
            </p>
          </div>

          <div className="flex items-center justify-end gap-2.5 pt-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={handleSkip}
              className="text-text-muted hover:text-white"
            >
              Пропустити
            </Button>
            <Button
              variant="primary"
              size="sm"
              rightIcon={<ArrowRight className="w-4 h-4" />}
              onClick={handleStartTour}
              className="shadow-[0_0_20px_rgba(16,185,129,0.3)]"
            >
              Почати навчання
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const step = TOUR_STEPS[currentStepIndex];
  const isLastStep = currentStepIndex === TOUR_STEPS.length - 1;
  const isInteractiveAction = step.interactive && stepPhase === 'action';

  // Responsive Tooltip Positioning & Strict Screen Clamping for Tablets & Desktops
  const pad = 16;
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1024;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 768;
  const tooltipW = Math.min(380, Math.max(280, vw - pad * 2));
  const tooltipH = measuredTooltipH;

  const hasValidTargetRect =
    targetRect !== null &&
    Number.isFinite(targetRect.left) &&
    Number.isFinite(targetRect.top) &&
    Number.isFinite(targetRect.width) &&
    Number.isFinite(targetRect.height) &&
    targetRect.width > 0 &&
    targetRect.height > 0;

  // Strict check that the target element is within the visible viewport bounds
  const isTargetInViewport =
    hasValidTargetRect &&
    targetRect !== null &&
    targetRect.bottom > 0 &&
    targetRect.top < vh &&
    targetRect.right > 0 &&
    targetRect.left < vw;

  let isPositionedCentered = true;
  let arrowPlacement: 'top' | 'bottom' | 'left' | 'right' | 'none' = 'none';
  let arrowLeft = 20;
  let arrowTop = 20;

  let tooltipStyle: React.CSSProperties = {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: `${tooltipW}px`,
    zIndex: 70,
    opacity: 1,
    visibility: 'visible',
    pointerEvents: 'auto',
  };

  if (isTargetInViewport && targetRect) {
    const targetCenterX = targetRect.left + targetRect.width / 2;
    const targetCenterY = targetRect.top + targetRect.height / 2;
    const preferred =
      stepPhase === 'observation' && step.observationPreferredPlacement
        ? step.observationPreferredPlacement
        : step.preferredPlacement || 'bottom';

    const spaceBelow = vh - targetRect.bottom - pad;
    const spaceAbove = targetRect.top - pad;
    const spaceLeft = targetRect.left - pad;
    const spaceRight = vw - targetRect.right - pad;

    let computedTop = targetRect.bottom + 14;
    let computedLeft = targetCenterX - tooltipW / 2;

    if (preferred === 'bottom') {
      if (spaceBelow >= tooltipH || spaceBelow >= spaceAbove) {
        computedTop = targetRect.bottom + 14;
        arrowPlacement = 'top';
      } else {
        computedTop = targetRect.top - tooltipH - 14;
        arrowPlacement = 'bottom';
      }
    } else if (preferred === 'top') {
      if (spaceAbove >= tooltipH || spaceAbove >= spaceBelow) {
        computedTop = targetRect.top - tooltipH - 14;
        arrowPlacement = 'bottom';
      } else {
        computedTop = targetRect.bottom + 14;
        arrowPlacement = 'top';
      }
    } else if (preferred === 'left') {
      if (spaceLeft >= tooltipW) {
        computedLeft = targetRect.left - tooltipW - 14;
        computedTop = targetCenterY - tooltipH / 2;
        arrowPlacement = 'right';
      } else if (spaceRight >= tooltipW) {
        computedLeft = targetRect.right + 14;
        computedTop = targetCenterY - tooltipH / 2;
        arrowPlacement = 'left';
      } else {
        computedTop = targetRect.bottom + 14;
        arrowPlacement = 'top';
      }
    } else if (preferred === 'right') {
      if (spaceRight >= tooltipW) {
        computedLeft = targetRect.right + 14;
        computedTop = targetCenterY - tooltipH / 2;
        arrowPlacement = 'left';
      } else if (spaceLeft >= tooltipW) {
        computedLeft = targetRect.left - tooltipW - 14;
        computedTop = targetCenterY - tooltipH / 2;
        arrowPlacement = 'right';
      } else {
        computedTop = targetRect.bottom + 14;
        arrowPlacement = 'top';
      }
    }

    // Strict tablet and desktop boundary clamping: [16px, innerWidth - tooltipWidth - 16px]
    const clampedLeft = Math.max(pad, Math.min(vw - tooltipW - pad, computedLeft));
    const clampedTop = Math.max(pad, Math.min(vh - tooltipH - pad, computedTop));

    if (Number.isFinite(clampedLeft) && Number.isFinite(clampedTop)) {
      isPositionedCentered = false;

      // Arrow alignment pointing directly to target element's center
      if (arrowPlacement === 'top' || arrowPlacement === 'bottom') {
        arrowLeft = Math.max(24, Math.min(tooltipW - 40, targetCenterX - clampedLeft - 8));
      } else if (arrowPlacement === 'left' || arrowPlacement === 'right') {
        arrowTop = Math.max(24, Math.min(tooltipH - 40, targetCenterY - clampedTop - 8));
      }

      tooltipStyle = {
        position: 'fixed',
        top: `${clampedTop}px`,
        left: `${clampedLeft}px`,
        transform: 'none',
        width: `${tooltipW}px`,
        zIndex: 70,
        opacity: 1,
        visibility: 'visible',
        pointerEvents: 'auto',
      };
    }
  }

  // Fallback: If target element is not found, off-screen, or layout is settling, ALWAYS render centered
  if (isPositionedCentered) {
    tooltipStyle = {
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: `${tooltipW}px`,
      zIndex: 70,
      opacity: 1,
      visibility: 'visible',
      pointerEvents: 'auto',
    };
  }

  const stepTitle = isInteractiveAction ? step.actionTitle : step.observationTitle;
  const stepDescription = isInteractiveAction ? step.actionDescription : step.observationDescription;
  const stepHint = isInteractiveAction ? step.actionHint : step.observationHint;

  return (
    <div className="fixed inset-0 z-50 pointer-events-auto">
      {/* High-visibility always-accessible Exit / Close button (z-[100]) */}
      <button
        type="button"
        onClick={handleSkip}
        className="fixed top-3 right-3 sm:top-4 sm:right-4 z-[100] flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-slate-900/95 hover:bg-slate-800 text-text-muted hover:text-white border border-white/20 hover:border-accent-neon/50 shadow-2xl backdrop-blur-xl text-xs font-semibold transition-all cursor-pointer select-none group pointer-events-auto"
        title="Завершити або пропустити навчання (Esc)"
        aria-label="Завершити або пропустити навчання"
      >
        <span className="text-text-secondary group-hover:text-white hidden sm:inline">Закрити тур</span>
        <X className="w-4 h-4 text-accent-neon" />
      </button>

      {/* SVG Dimmed Mask with Spotlight Cutout (Strict SVG 1.1 presentation attributes for WebKit/Safari) */}
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="fixed inset-0 w-full h-full pointer-events-none z-50"
        width="100%"
        height="100%"
      >
        <defs>
          <mask
            id="spotlight-mask"
            maskUnits="userSpaceOnUse"
            maskContentUnits="userSpaceOnUse"
          >
            <rect width="100%" height="100%" fill="white" />
            {isTargetInViewport && targetRect && (
              <rect
                x={Math.max(0, targetRect.left - 6)}
                y={Math.max(0, targetRect.top - 6)}
                width={targetRect.width + 12}
                height={targetRect.height + 12}
                rx={12}
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect
          width="100%"
          height="100%"
          fill="#0A0F1D"
          fillOpacity={0.84}
          mask="url(#spotlight-mask)"
        />
      </svg>

      {/* Target Element Interactive Spotlight Box & Indicator */}
      {isTargetInViewport && targetRect && (
        <div
          onClick={isInteractiveAction ? handleExecuteAction : undefined}
          className={`
            fixed z-50 transition-all duration-300 rounded-xl
            ${
              isInteractiveAction
                ? 'cursor-pointer pointer-events-auto border-2 border-cyan-400 shadow-[0_0_30px_rgba(6,182,212,0.85)] animate-pulse'
                : 'pointer-events-none border-2 border-emerald-400/80 shadow-[0_0_20px_rgba(16,185,129,0.5)]'
            }
          `}
          style={{
            top: targetRect.top - 6,
            left: targetRect.left - 6,
            width: targetRect.width + 12,
            height: targetRect.height + 12,
          }}
          title={
            isInteractiveAction
              ? 'Натисніть сюди для взаємодії'
              : 'Результат виконання поточної дії'
          }
        >
          {/* Action Callout Indicator: Animated bounce badge */}
          {isInteractiveAction && (
            <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-2.5 py-0.5 rounded-full bg-cyan-400 text-slate-950 font-bold text-[10.5px] flex items-center gap-1 shadow-lg shadow-cyan-500/50 animate-bounce select-none whitespace-nowrap">
              <ArrowDown className="w-3 h-3 text-slate-950 stroke-[2.5]" />
              <span>Натисніть сюди</span>
            </div>
          )}

          {/* Observation Callout Indicator */}
          {!isInteractiveAction && (
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full bg-emerald-500 text-slate-950 font-bold text-[9.5px] flex items-center gap-1 shadow-md shadow-emerald-500/40 select-none whitespace-nowrap">
              <CheckCircle2 className="w-3 h-3 text-slate-950" />
              <span>Результат дії</span>
            </div>
          )}
        </div>
      )}

      {/* Directional Pointer Arrow & Tooltip Card */}
      <div
        ref={tooltipRef}
        data-tour="tour-tooltip"
        style={tooltipStyle}
        className={`
          rounded-2xl bg-slate-900/95 border border-cyan-500/40 p-5 shadow-[0_10px_45px_rgba(0,0,0,0.88)] backdrop-blur-xl space-y-4 animate-fadeIn pointer-events-auto max-h-[calc(100vh-32px)] overflow-y-auto
          ${isPositionedCentered ? 'fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[70]' : 'fixed z-[70]'}
        `}
      >
        {/* Directional pointer arrow (only rendered when anchored to target element in viewport) */}
        {isTargetInViewport && !isPositionedCentered && arrowPlacement !== 'none' && (
          <div
            style={{
              position: 'absolute',
              ...(arrowPlacement === 'top' && { top: '-9px', left: `${arrowLeft}px` }),
              ...(arrowPlacement === 'bottom' && {
                bottom: '-9px',
                left: `${arrowLeft}px`,
                transform: 'rotate(180deg)',
              }),
              ...(arrowPlacement === 'left' && {
                left: '-9px',
                top: `${arrowTop}px`,
                transform: 'rotate(-90deg)',
              }),
              ...(arrowPlacement === 'right' && {
                right: '-9px',
                top: `${arrowTop}px`,
                transform: 'rotate(90deg)',
              }),
            }}
            className="w-4 h-2.5 pointer-events-none drop-shadow-[0_0_8px_rgba(6,182,212,0.7)]"
          >
            <svg
              viewBox="0 0 16 10"
              className="w-full h-full fill-slate-900 stroke-cyan-400 stroke-[1.5]"
            >
              <polygon points="8,0 16,10 0,10" />
            </svg>
          </div>
        )}

        {/* Card Header: Step number, phase indicator, Close X */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="w-6 h-6 rounded-lg bg-cyan-500/20 text-cyan-400 font-mono font-bold text-xs flex items-center justify-center border border-cyan-500/40">
              {currentStepIndex + 1}
            </span>
            <span className="text-[11px] font-semibold text-text-muted uppercase tracking-wider">
              Крок {currentStepIndex + 1} з {TOUR_STEPS.length}
            </span>
            {isInteractiveAction ? (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-300 font-medium flex items-center gap-1 border border-cyan-500/30">
                <MousePointerClick className="w-3 h-3 text-cyan-400" />
                <span>Дія</span>
              </span>
            ) : (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/20 text-accent-neon font-medium flex items-center gap-1 border border-emerald-500/30">
                <Eye className="w-3 h-3 text-accent-neon" />
                <span>Огляд</span>
              </span>
            )}
          </div>

          <button
            type="button"
            onClick={handleSkip}
            className="w-7 h-7 rounded-lg bg-surface-hover hover:bg-slate-800 text-text-muted hover:text-white flex items-center justify-center transition-colors cursor-pointer"
            title="Закрити навчання"
            aria-label="Закрити навчання"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Card Content: Title, Description, Hint */}
        <div className="space-y-1.5">
          <h4 className="text-sm font-bold text-white flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5 text-accent-neon shrink-0" />
            <span>{stepTitle}</span>
          </h4>
          <p className="text-xs text-text-secondary leading-relaxed">{stepDescription}</p>
          {stepHint && (
            <div className="text-[11px] text-cyan-300/90 bg-cyan-950/40 border border-cyan-500/20 rounded-lg p-2 font-medium">
              💡 {stepHint}
            </div>
          )}
        </div>

        {/* Card Footer Navigation */}
        <div className="flex items-center justify-between pt-1 border-t border-white/10">
          <button
            type="button"
            onClick={handleSkip}
            className="text-[11px] text-text-muted hover:text-white transition-colors cursor-pointer"
          >
            Пропустити
          </button>

          <div className="flex items-center gap-2">
            {(currentStepIndex > 0 || stepPhase === 'observation') && (
              <Button
                variant="secondary"
                size="sm"
                onClick={handlePrev}
                leftIcon={<ArrowLeft className="w-3.5 h-3.5" />}
              >
                Назад
              </Button>
            )}

            {isInteractiveAction ? (
              <Button
                variant="primary"
                size="sm"
                onClick={handleExecuteAction}
                rightIcon={<ArrowRight className="w-3.5 h-3.5" />}
                className="shadow-[0_0_15px_rgba(6,182,212,0.4)] bg-cyan-500 hover:bg-cyan-400 text-slate-950 border-cyan-300"
              >
                {step.actionButtonText || 'Виконати дію ➔'}
              </Button>
            ) : (
              <Button
                variant="primary"
                size="sm"
                onClick={handleNext}
                rightIcon={
                  isLastStep ? <Check className="w-3.5 h-3.5" /> : <ArrowRight className="w-3.5 h-3.5" />
                }
                className="shadow-[0_0_15px_rgba(16,185,129,0.3)]"
              >
                {isLastStep
                  ? step.observationButtonText || 'Завершити'
                  : step.observationButtonText || 'Далі ➔'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
